import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  QueryConstraint,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
} from '@angular/fire/firestore';
import { Observable, from } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

type CacheEntry<T> = {
  createdAt: number;
  stream$: Observable<T>;
};

@Injectable({
  providedIn: 'root',
})
export class DataLoaderService {
  private firestore = inject(Firestore);
  private cache = new Map<string, CacheEntry<any>>();
  private readonly defaultTtlMs = 10 * 60 * 1000;

  listOnce<T = any>(
    collectionPath: string,
    constraints: QueryConstraint[] = [],
    cacheKey = '',
    ttlMs = this.defaultTtlMs,
  ): Observable<Array<T & { id: string }>> {
    const key = this.key('list', collectionPath, cacheKey || String(constraints.length));

    return this.getCached(key, ttlMs, () => {
      const ref = collection(this.firestore, collectionPath);
      const qRef = constraints.length ? query(ref, ...constraints) : ref;

      return from(getDocs(qRef as any)).pipe(
        map((snap: any) =>
          snap.docs.map((d: any) => ({
            id: d.id,
            ...d.data(),
          })),
        ),
      );
    });
  }

  docOnce<T = any>(
    collectionPath: string,
    id: string,
    ttlMs = this.defaultTtlMs,
  ): Observable<(T & { id: string }) | null> {
    const key = this.key('doc', collectionPath, id);

    return this.getCached(key, ttlMs, () =>
      from(getDoc(doc(this.firestore, collectionPath, id))).pipe(
        map((snap: any) =>
          snap.exists()
            ? ({
                id: snap.id,
                ...snap.data(),
              } as T & { id: string })
            : null,
        ),
      ),
    );
  }

  listOncePromise<T = any>(
    collectionPath: string,
    constraints: QueryConstraint[] = [],
    cacheKey = '',
    ttlMs = this.defaultTtlMs,
  ): Promise<Array<T & { id: string }>> {
    return this.toPromise(this.listOnce<T>(collectionPath, constraints, cacheKey, ttlMs));
  }

  docOncePromise<T = any>(
    collectionPath: string,
    id: string,
    ttlMs = this.defaultTtlMs,
  ): Promise<(T & { id: string }) | null> {
    return this.toPromise(this.docOnce<T>(collectionPath, id, ttlMs));
  }

  invalidate(prefix = ''): void {
    if (!prefix) {
      this.cache.clear();
      return;
    }

    Array.from(this.cache.keys())
      .filter((key) => key.includes(prefix))
      .forEach((key) => this.cache.delete(key));
  }

  private getCached<T>(
    key: string,
    ttlMs: number,
    factory: () => Observable<T>,
  ): Observable<T> {
    const existing = this.cache.get(key);
    const now = Date.now();

    if (existing && now - existing.createdAt < ttlMs) {
      return existing.stream$ as Observable<T>;
    }

    const stream$ = factory().pipe(shareReplay(1));
    this.cache.set(key, { createdAt: now, stream$ });
    return stream$;
  }

  private toPromise<T>(source$: Observable<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let sub: any;
      let unsubscribeAfterSubscribe = false;

      sub = source$.subscribe({
        next: (value) => {
          resolve(value);
          if (sub) {
            sub.unsubscribe();
          } else {
            unsubscribeAfterSubscribe = true;
          }
        },
        error: reject,
      });

      if (unsubscribeAfterSubscribe) {
        sub.unsubscribe();
      }
    });
  }

  private key(...parts: string[]): string {
    return parts.map((part) => encodeURIComponent(part || '')).join(':');
  }
}
